import { View, Text, Platform } from "react-native";
import { GuidePageHeader, GuideCard, GuideTip, GuideStep, GuideNav, getGuideNav } from "./_components";

const PAGE_HREF = "/(web)/guide/agenda";

export default function AgendaPage() {
  if (Platform.OS !== "web") return null;
  const nav = getGuideNav(PAGE_HREF);

  return (
    <View>
      <GuidePageHeader
        icon="today"
        title="Agenda"
        subtitle="Bekijk het programma, maak eigen evenementen aan en mis niets dankzij herinneringen."
        color="#10B981"
      />

      {/* Overzicht */}
      <Text className="text-xl font-bold text-gray-900 mb-4">De Agenda</Text>
      <Text className="text-base text-gray-600 leading-6 mb-6">
        De agenda toont alle evenementen: van het weekendprogramma tot persoonlijke afspraken. 
        Je kunt de agenda bekijken per dag, week of als lijst. Evenementen zijn kleurgecodeerd 
        per categorie.
      </Text>

      {/* Categorieën */}
      <Text className="text-xl font-bold text-gray-900 mb-4 mt-8">Categorieën</Text>
      <Text className="text-base text-gray-600 leading-6 mb-6">
        Evenementen zijn onderverdeeld in categorieën zodat je snel kunt zien wat er op het 
        programma staat:
      </Text>

      <View className="mb-6">
        <GuideCard
          icon="game-controller"
          title="Activiteit"
          description="Sportieve of sociale activiteiten tijdens het weekend."
          color="#3B82F6"
        />
        <GuideCard
          icon="restaurant"
          title="Maaltijd"
          description="Ontbijt, lunch, diner en snacks."
          color="#F59E0B"
        />
        <GuideCard
          icon="trophy"
          title="Spel"
          description="Geplande spelrondes en toernooien."
          color="#EF4444"
        />
        <GuideCard
          icon="ribbon"
          title="Ceremonie"
          description="Prijsuitreikingen, toespraken en speciale momenten."
          color="#8B5CF6"
        />
        <GuideCard
          icon="cafe"
          title="Vrije tijd"
          description="Momenten om te ontspannen of vrij te besteden."
          color="#10B981"
        />
        <GuideCard
          icon="sunny"
          title="Familie Dag / Weekend"
          description="Het overkoepelende evenement van de Familie Dag of het weekend zelf."
          color="#EC4899"
        />
      </View>

      {/* Evenement aanmaken */}
      <Text className="text-xl font-bold text-gray-900 mb-4 mt-8">Evenement Aanmaken</Text>
      <Text className="text-base text-gray-600 leading-6 mb-6">
        Iedereen kan eigen evenementen aanmaken! Je kunt kiezen uit drie zichtbaarheidsniveaus:
      </Text>

      <View className="mb-6">
        <GuideCard
          icon="lock-closed"
          title="Privé"
          description="Alleen zichtbaar voor jou. Handig voor persoonlijke herinneringen of notities."
          color="#6B7280"
        />
        <GuideCard
          icon="people"
          title="Team"
          description="Zichtbaar voor je teamgenoten. Gebruik dit voor teamactiviteiten of afspraken."
          color="#3B82F6"
        />
        <GuideCard
          icon="globe"
          title="Iedereen"
          description="Zichtbaar voor alle familieleden. Geschikt voor evenementen waar iedereen welkom is."
          color="#10B981"
        />
      </View>

      <GuideStep
        step={1}
        title="Tik op '+' in de agenda"
        description="Open de agenda en tik op de plus-knop om een nieuw evenement te maken."
        color="#10B981"
      />
      <GuideStep
        step={2}
        title="Vul de details in"
        description="Geef een titel, beschrijving, datum/tijd, locatie en categorie op. Kies een kleur en optioneel een animatie."
        color="#10B981"
      />
      <GuideStep
        step={3}
        title="Stel de zichtbaarheid in"
        description="Kies of het evenement privé, voor je team of voor iedereen zichtbaar is."
        color="#10B981"
      />
      <GuideStep
        step={4}
        title="Opslaan"
        description="Sla het evenement op. Het verschijnt direct in de agenda."
        color="#10B981"
      />

      {/* Onthullingen */}
      <Text className="text-xl font-bold text-gray-900 mb-4 mt-8">Onthullingen (Reveals)</Text>
      <Text className="text-base text-gray-600 leading-6 mb-6">
        De commissie kan evenementen gedeeltelijk verbergen en later onthullen. Dit werkt 
        als een verrassing voor de familie! Er zijn verschillende manieren om een onthulling 
        te doen:
      </Text>

      <View className="mb-6">
        <GuideCard
          icon="time"
          title="Countdown"
          description="Een aftelling tot het moment van onthulling. De spanning bouwt op naarmate de tijd nadert!"
          color="#EF4444"
        />
        <GuideCard
          icon="bulb"
          title="Hints"
          description="De commissie kan hints geven voorafgaand aan de onthulling. Familieleden kunnen raden wat er gaat komen."
          color="#F59E0B"
        />
        <GuideCard
          icon="flash"
          title="Uitdaging-gebaseerd"
          description="Voltooi een uitdaging om de onthulling te activeren. Hoe meer je doet, hoe eerder je het weet!"
          color="#8B5CF6"
        />
      </View>

      <GuideTip title="Wat kan verborgen worden?">
        De commissie kan kiezen om de locatie, beschrijving, titel of alles te verbergen. 
        Verborgen informatie wordt vervangen door een placeholder tekst tot de onthulling.
      </GuideTip>

      {/* Herinneringen */}
      <Text className="text-xl font-bold text-gray-900 mb-4 mt-8">Herinneringen</Text>
      <Text className="text-base text-gray-600 leading-6 mb-6">
        Je kunt herinneringen instellen voor evenementen. Standaard word je 15 of 30 minuten 
        van tevoren herinnerd via een push-melding. Dit kun je per evenement aanpassen.
      </Text>

      {/* Agenda exporteren */}
      <Text className="text-xl font-bold text-gray-900 mb-4 mt-8">Agenda Exporteren</Text>
      <Text className="text-base text-gray-600 leading-6 mb-6">
        Je kunt evenementen exporteren naar je eigen agenda-app. Dit werkt met:
      </Text>

      <View className="mb-6">
        <GuideCard icon="logo-google" title="Google Calendar" description="Exporteer direct naar je Google Agenda." color="#4285F4" />
        <GuideCard icon="logo-apple" title="Apple Calendar" description="Voeg toe aan je iPhone/iPad agenda." color="#1F2937" />
        <GuideCard icon="calendar" title="Overige (ICS)" description="Download een ICS-bestand dat in de meeste agenda-apps werkt." color="#10B981" />
      </View>

      <GuideNav {...nav} />
    </View>
  );
}
